/**
 * PATH: packages/kanban-host/src/allocation/useAllocateToKanbanMenuData.ts
 * FILE_ID: AIHUB-KANBAN-ALLOC-MENUDATA-001
 * PURPOSE: Resolve the ordered board list that powers the "Allocate to
 *          Kanban" right-click submenu. Returns three buckets — pinned,
 *          recent, all — each filtered to non-archived boards the user
 *          has at least EDITOR access to.
 * WHY: Module B of EMAIL_KANBAN_ALLOCATION_PLAN.md. The menu must
 *      surface the boards the operator actually uses first (pinned,
 *      then recently-touched); the long tail goes under "All boards"
 *      so even fresh boards stay reachable.
 * FLOW:
 *   ┌────────────────┐    ┌──────────────────────────┐    ┌────────────────┐
 *   │ KanbanApi.list │ →  │ filter + dedupe + sort   │ →  │ menu buckets  │
 *   │ Boards()       │    │ (archived, RBAC, prefs)  │    │ {pinned,recent,│
 *   └────────────────┘    └──────────────────────────┘    │  all}          │
 *                                                         └────────────────┘
 *
 * DEPENDENCIES:
 *   - @quotech/ui/kanban : board types + theme resolver for menu swatches
 *   - @tanstack/react-query : caching (shares the cache key with
 *     <KanbanBoardList> so the menu is warm whenever the user has
 *     opened the rail)
 *   - ./boardPrefs : localStorage-backed pinned + recent ids
 *
 * RELATED:
 *   - apps/newmark/src/features/email/components/EmailContextMenu.tsx
 *     (consumer)
 *   - packages/ui/src/kanban/components/KanbanBoardList.tsx
 *     (cache-key sibling — `['kanban-boards']`)
 */

import { useEffect, useMemo, useSyncExternalStore } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import type { Board, KanbanApi } from '@quotech/ui/kanban'
import { getActiveLaneCount, getBoardTheme, useBoardDefaults } from '@quotech/ui/kanban'

import {
  readPinnedBoardIds,
  readRecentBoardIds,
  subscribePrefs,
} from './boardPrefs'
import { buildBoardTree, type BoardTreeNode } from './boardTree'

export interface AllocateMenuBoard {
  id: string
  name: string
  /** Resolved board theme accent (hex). `null` when no theme. */
  swatch: string | null
  /** Lane count from the cached board payload — drives the
   *  "(no lanes)" disabled state when zero. */
  laneCount: number
  /** The raw Board so consumers don't have to re-fetch for theme. */
  board: Board
}

export interface AllocateMenuData {
  /** FEAT-2026-05-24-KANBAN-BOARD-HOME — the operator's designated
   *  "home board" (when set + writable + non-archived). Distinct
   *  from Pinned / Recent: a pin is a "keep this board surfaced"
   *  toggle, a recent is "I touched this board lately", a home is
   *  the SINGLE board this operator wants to default to. Rendered
   *  as a top-of-list bucket with a Home icon + one-click "drop
   *  into <primary lane>" action so unassigned email allocations
   *  land in the home board by default. Null when no home is set
   *  OR when the set home is no longer visible to the caller. */
  home: AllocateMenuBoard | null
  pinned: AllocateMenuBoard[]
  recent: AllocateMenuBoard[]
  /** Every other board the user can write to, sorted by
   *  `display_order` then name. KEPT for back-compat with consumers
   *  that don't want the tree view (and used as the input to
   *  buildBoardTree below). */
  all: AllocateMenuBoard[]
  /** Hierarchical view of `all` — boards whose names use ` > ` as a
   *  path separator are grouped into nested submenus. Boards
   *  without ` > ` appear in `tree.boards` as top-level leaves. The
   *  renderer prefers this over `all` so operators can scan large
   *  board lists by year / quarter / project. See
   *  `./boardTree.ts` for the construction rules + their tests. */
  tree: BoardTreeNode
  /** FEAT-2026-06-03-KANBAN-JOB-SUBBOARD-ROUTING — real sub-boards
   *  (`Board.parent_board`) keyed by their parent board id. Sub-boards
   *  are REMOVED from `home`/`pinned`/`recent`/`all`/`tree` (so they
   *  never appear as flat top-level entries) and instead rendered
   *  nested under their parent's submenu — mirroring how the Kanban
   *  left rail (`KanbanBoardList`) groups by `parent_board`. The
   *  allocate menu previously ignored this FK entirely and only faked
   *  hierarchy from board-name ` > ` paths (see boardTree.ts), so a
   *  promoted sub-board showed up flat and you couldn't navigate
   *  parent → sub-board → lane. Each child value is already sorted by
   *  `display_order`. */
  childrenByParentId: Map<string, AllocateMenuBoard[]>
  isLoading: boolean
  isError: boolean
  /** Convenience for the menu's empty state. */
  totalCount: number
}

// ──────────────────────────────────────────────────────────
// Pure helpers (I → O)
// ──────────────────────────────────────────────────────────

/**
 * Pick the boards a user can ALLOCATE into.
 *
 *  Why we filter client-side:
 *    The current `/api/kanban/boards/` list response already filters
 *    to the requesting user's BoardMembership rows on the BE — i.e.
 *    we get back the boards the user can see. The menu wants a
 *    SLIGHTLY stricter cut: only non-archived boards (archived are
 *    historical and shouldn't appear in an allocation chooser even
 *    if the user technically retains EDITOR rights).
 *
 *  Future BE work (tracked in plan §B.2):
 *    add a ?writable=1 param so we don't have to trust the FE on
 *    the RBAC cut. For now the BE rejects an allocate-email call
 *    from a non-editor anyway, so the worst case is a 403 toast.
 */
export function pickAllocatableBoards(boards: Board[]): Board[] {
  return boards.filter((b) => !b.archived)
}

/**
 * Pure I→O — scope the board list to ONE organisation.
 *
 * BUG-2026-07-15-ALLOCATE-PICKER-CROSS-ORG (BUG-0019 family): the
 * board list is membership-union cross-org by design, so a multi-org
 * operator's allocate picker showed EVERY org's boards (SAM
 * `Job #10002xx` inside the Newmark/Quotech Emails hub) even though
 * allocating a cross-org email 403s on the BE. The allocate surfaces
 * must only offer boards the allocation can succeed on — boards in
 * the active org.
 *
 * Filing Integrity D4 — failClosed (default for allocate): when
 * `orgId` matches ZERO boards, return [] and let the UI CTA explain
 * (wrong org context / re-home needed). The legacy degrade-to-union
 * path remains available via `{ failClosed: false }` for hosts that
 * still hit the master/local org-id split-brain
 * (BUG-2026-05-13).
 *
 *   in  → ([{org:5},{org:8}], 8)   out → [{org:8}]
 *   in  → ([{org:5},{org:8}], 99, failClosed) out → []
 *   in  → ([{org:5},{org:8}], 99, !failClosed) out → [{org:5},{org:8}]
 *   in  → ([{org:5}], null)        out → [{org:5}]
 */
export function scopeBoardsToOrg(
  boards: Board[],
  orgId: number | null | undefined,
  opts?: { failClosed?: boolean },
): Board[] {
  if (orgId == null) return boards
  const scoped = boards.filter((b) => b.organisation === orgId)
  if (scoped.length > 0) return scoped
  return opts?.failClosed === false ? boards : []
}

/**
 * Pure I→O — order boards the same way the Kanban rail + gallery
 * order them: by the BE-managed `display_order`, ties broken by name.
 *
 * WHY this matters (regression we shipped 2026-05-12 and the user
 * called out 2026-05-13): the right-click submenu's "All" bucket was
 * sorting alphabetically, which placed e.g. an "Acme — Foundations"
 * job board above "Triage Inbox" purely on the leading letter. The
 * operator's mental model of "where do my boards live" comes from
 * the rail, and the rail is `display_order`. Using a different key
 * in the menu meant the operator couldn't find the board they
 * expected at the top.
 *
 * `display_order` is a stable integer assigned by the BE
 * (`email_kanban/models/board.py::Board.display_order`) — lower
 * wins. Ties broken by `name.localeCompare` so the order is
 * deterministic across page reloads even for freshly created boards
 * that share `display_order = 0` until the first reorder.
 */
export function compareBoardsByDisplayOrder(a: Board, b: Board): number {
  // Treat missing display_order as "very end" so unrolled-out boards
  // sink below the user's own boards.
  const ao = typeof a.display_order === 'number' ? a.display_order : Number.MAX_SAFE_INTEGER
  const bo = typeof b.display_order === 'number' ? b.display_order : Number.MAX_SAFE_INTEGER
  if (ao !== bo) return ao - bo
  return a.name.localeCompare(b.name)
}

/** @deprecated use {@link compareBoardsByDisplayOrder} — kept as a
 *  re-export so existing imports compile during the deprecation window. */
export const compareBoardsByName = compareBoardsByDisplayOrder

export function deriveSwatch(board: Board): string | null {
  // 1. Per-lane colours often live in lanes[0] for visual continuity.
  //    But the BOARD swatch is the source of identity — fall back to
  //    the lane only when no board theme is set so JOB / TRELLO
  //    boards still get their auto-colour.
  const theme = getBoardTheme(board)
  if (theme?.accent) return theme.accent
  if (theme?.bg) return theme.bg
  const firstLane = board.lanes?.[0]
  if (firstLane?.color) return firstLane.color
  return null
}

function toMenuBoard(board: Board): AllocateMenuBoard {
  return {
    id: board.id,
    name: board.name,
    swatch: deriveSwatch(board),
    // PERF-2026-06-14-KANBAN-LANE-COUNT — prefer the server-computed
    // active lane count (LIST endpoint) and only fall back to the
    // embedded `lanes` array when it's absent, so the menu keeps an
    // accurate "(no lanes)" gate after the list payload stops shipping
    // per-board lanes.
    laneCount: getActiveLaneCount(board),
    board,
  }
}

/**
 * Split the writable board list into the menu buckets using the
 * user's localStorage prefs + their server-backed home pointer.
 *
 * Bucket priority:
 *
 *   home   →   pinned   →   recent   →   all
 *
 * An id can only appear in ONE bucket so the menu never duplicates.
 * (The home board is silently SKIPPED from pinned/recent/all when
 * present; the operator already sees it at the top, repeating it
 * lower down is noise.)
 *
 * `homeBoardId` may be `null` for tenants without a home set OR for
 * older host bundles whose useBoardHome graceful-degrades to null.
 * In that case the function behaves exactly like the pre-home
 * version.
 */
export function buildMenuBuckets(
  boards: Board[],
  pinnedIds: string[],
  recentIds: string[],
  homeBoardId: string | null = null,
): Pick<AllocateMenuData, 'home' | 'pinned' | 'recent' | 'all'> {
  const byId = new Map(boards.map((b) => [b.id, b]))
  const used = new Set<string>()

  let home: AllocateMenuBoard | null = null
  if (homeBoardId) {
    const homeBoard = byId.get(homeBoardId)
    if (homeBoard) {
      home = toMenuBoard(homeBoard)
      used.add(homeBoardId)
    }
  }

  const pinned: AllocateMenuBoard[] = []
  for (const id of pinnedIds) {
    if (used.has(id)) continue
    const b = byId.get(id)
    if (b) {
      pinned.push(toMenuBoard(b))
      used.add(id)
    }
  }

  const recent: AllocateMenuBoard[] = []
  for (const id of recentIds) {
    if (used.has(id)) continue
    const b = byId.get(id)
    if (b) {
      recent.push(toMenuBoard(b))
      used.add(id)
    }
  }

  const all: AllocateMenuBoard[] = boards
    .filter((b) => !used.has(b.id))
    .sort(compareBoardsByDisplayOrder)
    .map(toMenuBoard)

  return { home, pinned, recent, all }
}

/**
 * FEAT-2026-06-03-KANBAN-JOB-SUBBOARD-ROUTING — pure I→O.
 *
 * Partition a flat writable-board list into TOP-LEVEL boards and a
 * `parent id → sub-boards` map, using the real `Board.parent_board`
 * FK (NOT the board-name ` > ` path convention in boardTree.ts).
 *
 * A board is treated as a sub-board only when its `parent_board`
 * points at another board that is ALSO present + visible in this list.
 * An "orphan" sub-board (parent archived / not visible to this user)
 * falls back to top-level so it's never lost from the menu — same
 * safety the rail relies on.
 *
 * Children are sorted by `display_order` (ties by name) so the nested
 * order matches the rail. Depth is capped at 2 by the backend, so a
 * sub-board never itself has children.
 */
export function splitSubBoards(boards: Board[]): {
  topLevel: Board[]
  childrenByParentId: Map<string, AllocateMenuBoard[]>
} {
  const idSet = new Set(boards.map((b) => b.id))
  const topLevel: Board[] = []
  const rawChildren = new Map<string, Board[]>()

  for (const b of boards) {
    const parentId = b.parent_board
    if (parentId && idSet.has(parentId)) {
      const list = rawChildren.get(parentId)
      if (list) list.push(b)
      else rawChildren.set(parentId, [b])
    } else {
      topLevel.push(b)
    }
  }

  const childrenByParentId = new Map<string, AllocateMenuBoard[]>()
  for (const [parentId, list] of rawChildren) {
    childrenByParentId.set(
      parentId,
      list.slice().sort(compareBoardsByDisplayOrder).map(toMenuBoard),
    )
  }

  return { topLevel, childrenByParentId }
}

// ──────────────────────────────────────────────────────────
// Hook
// ──────────────────────────────────────────────────────────

const EMPTY_BUCKETS: Pick<
  AllocateMenuData,
  'home' | 'pinned' | 'recent' | 'all' | 'childrenByParentId'
> = {
  home: null,
  pinned: [],
  recent: [],
  all: [],
  childrenByParentId: new Map(),
}

/** Pre-computed empty tree so the hook's first render (before query
 *  resolves) returns a stable object identity — avoids spurious
 *  re-renders in consumers that depend on `tree` referentially. */
const EMPTY_TREE: BoardTreeNode = {
  label: '',
  path: '',
  children: [],
  boards: [],
  leafCount: 0,
}

/**
 * React hook driving the submenu. The query is enabled on mount so
 * by the time the operator right-clicks an email row the list is
 * already cached.
 */
export function useAllocateToKanbanMenuData(args: {
  api: KanbanApi
  /** Master Quotech org id (drives the per-tenant pref namespace). */
  orgId: number | null
  /** When false the underlying network call is gated. Used to
   *  postpone the listBoards() round-trip until the menu actually
   *  opens for the first time, in case the host page never opens it. */
  enabled?: boolean
  /** Optional master Quotech user id. When provided, pinned + recent
   *  buckets use the per-(user, org) v2 prefs keys; when omitted,
   *  the v1 per-org keys (legacy contract) are used. Postmortem
   *  2026-05-19 follow-up #6 (qf!3196). */
  currentUserId?: number | string | null
}): AllocateMenuData {
  const { api, orgId, enabled = true, currentUserId = null } = args
  const queryClient = useQueryClient()

  // BUG-2026-07-15-ALLOCATE-PICKER-CROSS-ORG — the allocate menu is a
  // single-org surface (the allocate write rejects cross-org boards),
  // so scope both the FETCH (`?organisation=` narrowing param, ignored
  // by older BE bundles) and the CACHE KEY (a multi-org operator
  // switching workspace within staleTime must not see the previous
  // org's board list). Invalidations/prefetches that target the bare
  // ['kanban-boards'] prefix still match this key. When orgId is
  // unknown, keep the legacy unscoped contract.
  const query = useQuery({
    queryKey: orgId != null ? ['kanban-boards', orgId] : ['kanban-boards'],
    queryFn: () =>
      api.listBoards(
        orgId != null
          ? { organisationId: orgId, writable: true }
          : { writable: true },
      ),
    enabled,
    staleTime: 30_000,
  })

  // FEAT-2026-05-25-BOARD-DEFAULTS-PER-CATEGORY — operator's
  // per-category default board bag. Shared react-query cache
  // (`['kanban', 'board-defaults', orgId]`) with the rail's
  // <KanbanBoardContextMenu> wiring so a "Set as default for Email"
  // gesture on the rail immediately re-shapes this menu's top
  // bucket. Same org-scoping rationale as the previous useBoardHome
  // wiring: the hook is keyed by the MASTER Quotech org id on the
  // backend, but the allocate menu has historically been keyed by
  // the local sam-api org id (see
  // BUG-2026-05-13-ALLOCATE-MENU-PIN-SPLIT-BRAIN). We pass the
  // local orgId here too — the server enforces RBAC against the
  // master id internally via the session, so the local id is
  // sufficient for the per-tenant scope.
  //
  // Priority for the top bucket:
  //   defaults.EMAIL  →  defaults.HOME  →  null
  // i.e. an explicit EMAIL default always wins (operator set it
  // SPECIFICALLY for this workflow); fall through to HOME so the
  // pre-FEAT-2026-05-25 single-home behaviour is preserved for any
  // operator who hasn't picked an Email-specific default yet. The
  // useBoardDefaults capability-detection layer transparently falls
  // back to /board-home/ on older BE bundles so this swap is a
  // no-op for tenants that haven't deployed qb!3079 yet.
  const { defaults } = useBoardDefaults({
    api,
    organisationId: orgId ?? undefined,
  })
  const homeBoardId = defaults.EMAIL ?? defaults.HOME ?? null

  // useSyncExternalStore so the buckets re-derive when the user
  // pins / touches a board from anywhere else in the app (e.g.
  // the future "Pin this board" button on KanbanBoardView).
  const prefsVersion = useSyncExternalStore(
    subscribePrefs,
    () =>
      `${readPinnedBoardIds(orgId, currentUserId).join(',')}|${readRecentBoardIds(orgId, currentUserId).join(',')}`,
    () => '',
  )

  const buckets = useMemo(() => {
    if (!query.data) return EMPTY_BUCKETS
    // Client-side belt-and-braces for the server-side ?organisation=
    // cut. Allocate menus fail closed (empty) when the org id matches
    // no boards — see Filing Integrity D4 / scopeBoardsToOrg.
    const writable = pickAllocatableBoards(
      scopeBoardsToOrg(query.data, orgId, { failClosed: true }),
    )
    // Pull real sub-boards out of the flat buckets so they only ever
    // render nested under their parent (see splitSubBoards / the menu
    // renderer). Top-level boards still bucket into home/pinned/recent/all.
    const { topLevel, childrenByParentId } = splitSubBoards(writable)
    return {
      ...buildMenuBuckets(
        topLevel,
        readPinnedBoardIds(orgId, currentUserId),
        readRecentBoardIds(orgId, currentUserId),
        homeBoardId,
      ),
      childrenByParentId,
    }
    // prefsVersion is the snapshot that makes this memo aware of pref
    // changes; including it in the deps array preserves identity-stable
    // memoisation while still tracking external-store updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data, orgId, currentUserId, prefsVersion, homeBoardId])

  // Build the tree from the SAME `all` bucket the flat list used to
  // render. Pinned + Recent stay flat at the top — operators
  // explicitly want their favourites surfaced regardless of
  // hierarchy. The tree only structures the long tail.
  const tree = useMemo(() => {
    if (buckets.all.length === 0) return EMPTY_TREE
    return buildBoardTree(buckets.all)
  }, [buckets.all])

  // PERF-2026-06-03-ALLOCATE-MENU-LANE-WARM — eliminate the
  // per-board "Loading lanes…" hover stall the operator complained
  // about. Each <BoardRow> only fetches its lanes on first hover
  // (`useKanbanBoardLanes({ enabled: hovered })`), so the very first
  // hover over EVERY board used to pay a cold round-trip. We can't
  // afford to warm the long "All boards" tail (could be hundreds of
  // boards × a listLanes call each), but the buckets the operator
  // actually sees WITHOUT drilling — home + pinned + recent — are a
  // small, bounded set. Prefetch their lanes the moment the board
  // list resolves so hovering any surfaced board is instant. Uses
  // the SAME cache key + queryFn + staleTime as useKanbanBoardLanes
  // so the row's own query is a pure cache hit (no double fetch).
  const warmIds = useMemo(() => {
    const ids: string[] = []
    if (buckets.home) ids.push(buckets.home.id)
    for (const b of buckets.pinned) ids.push(b.id)
    for (const b of buckets.recent) ids.push(b.id)
    return ids
  }, [buckets.home, buckets.pinned, buckets.recent])

  useEffect(() => {
    if (!enabled || warmIds.length === 0) return
    for (const boardId of warmIds) {
      void queryClient.prefetchQuery({
        queryKey: ['kanban-lanes', boardId],
        queryFn: () => api.listLanes(boardId, { withCounts: true }),
        staleTime: 15_000,
      })
    }
  }, [enabled, warmIds, queryClient, api])

  const totalCount =
    (buckets.home ? 1 : 0)
    + buckets.pinned.length
    + buckets.recent.length
    + buckets.all.length

  return {
    ...buckets,
    tree,
    isLoading: query.isLoading,
    isError: query.isError,
    totalCount,
  }
}
